/* Ethereal overhaul (Oct 2026): small helpers on top of Dawn 11.
   - Review notes ([data-eth-todo]) show only while the theme is unpublished.
   - Product page: stack and save tiers and piece facts follow the selected variant.
   - Cart: "add one more piece" adds without leaving the cart and re-renders Dawn's cart sections.
   - Landing pages: curated stacks with a metal toggle, live stack price and one-tap "add the stack". */
(() => {
  const theme = (window.Shopify && window.Shopify.theme) || {};
  const showNotes = Boolean(window.Shopify && window.Shopify.designMode) || (theme.role && theme.role !== 'main');

  const revealNotes = (root = document) => {
    if (!showNotes) return;
    root.querySelectorAll('[data-eth-todo]').forEach((note) => note.removeAttribute('hidden'));
  };

  const money = (cents) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(cents) / 100);

  const cartAddUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json().then((data) => ({ ok: response.ok, data })));

  const showError = (element, message) => {
    if (!element) return;
    element.textContent = message || 'Sorry, that could not be added. Please try again.';
    element.removeAttribute('hidden');
  };

  /* Product page: swap the stack tiers and piece facts for the versions rendered with the new variant. */
  const followVariant = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      if (!data || !data.html) return;
      const section = document.getElementById(`MainProduct-${data.sectionId}`);
      if (!section) return;
      ['[data-eth-stack-tiers]', '[data-eth-piece-facts]'].forEach((selector) => {
        const current = section.querySelector(selector);
        const fresh = data.html.querySelector(selector);
        if (current && fresh) {
          current.replaceWith(fresh);
          revealNotes(fresh);
        }
      });
    });
  };

  /* Cart: Dawn's <cart-items> treats every change event inside it as a quantity change.
     Keep our own selects out of that, and keep the shown price in step with the option. */
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest && event.target.closest('[data-eth-addon-select]');
      if (!select) return;
      event.stopPropagation();
      const option = select.selectedOptions && select.selectedOptions[0];
      const price = select.closest('.eth-addon') && select.closest('.eth-addon').querySelector('[data-eth-addon-price]');
      if (option && price && option.dataset.price) price.textContent = option.dataset.price;
    },
    true
  );

  /* The cart page has <cart-items>, the cart drawer (v2) has <cart-drawer-items>: use the one the button is in */
  const cartItemsFor = (element) =>
    element && element.closest('cart-drawer')
      ? document.querySelector('cart-drawer-items')
      : document.querySelector('cart-items');

  const refreshCartSections = (state, cartItems) => {
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function' || !state.sections) {
      window.location.reload();
      return;
    }
    cartItems.getSectionsToRender().forEach((section) => {
      const host = document.getElementById(section.id);
      const html = state.sections[section.section];
      if (!host || !html) return;
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
    });
    revealNotes();
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-eth-addon-add]');
    if (!button) return;
    event.preventDefault();
    const card = button.closest('.eth-addon');
    const input = card && card.querySelector('[data-eth-addon-select]');
    const addons = button.closest('[data-eth-addons]');
    const error = addons && addons.querySelector('[data-eth-addon-error]');
    if (!input || !input.value) return;

    const cartItems = cartItemsFor(button);
    const sections =
      cartItems && typeof cartItems.getSectionsToRender === 'function'
        ? cartItems.getSectionsToRender().map((section) => section.section)
        : [];

    button.setAttribute('aria-busy', 'true');
    postJSON(cartAddUrl(), {
      items: [{ id: Number(input.value), quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    })
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          button.removeAttribute('aria-busy');
          showError(error, data.description || data.message);
          return;
        }
        refreshCartSections(data, cartItems);
      })
      .catch(() => window.location.reload());
  });

  /* Landing pages: curated stacks */
  const initBundle = (bundle) => {
    const pct = Number(bundle.dataset.pct) || 0;
    const selects = Array.from(bundle.querySelectorAll('[data-eth-piece-select]'));
    const addButton = bundle.querySelector('[data-eth-bundle-add]');
    const error = bundle.querySelector('[data-eth-bundle-error]');
    const setText = (selector, text) => {
      const element = bundle.querySelector(selector);
      if (element) element.textContent = text;
    };

    const update = () => {
      let total = 0;
      let after = 0;
      let complete = true;
      selects.forEach((select) => {
        const option = select.selectedOptions && select.selectedOptions[0];
        if (!option) {
          complete = false;
          return;
        }
        const price = Number(option.dataset.price) || 0;
        total += price;
        after += price - Math.floor((price * pct) / 100);
        const piece = select.closest('[data-eth-piece]');
        const priceLabel = piece && piece.querySelector('[data-eth-piece-price]');
        if (priceLabel) priceLabel.textContent = money(price);
      });
      setText('[data-eth-bundle-total]', money(total));
      setText('[data-eth-bundle-saving]', `−${money(total - after)}`);
      setText('[data-eth-bundle-after]', money(after));
      setText('[data-eth-bundle-button-price]', `· ${money(after)}`);
      if (addButton) addButton.disabled = !complete;
    };

    const applyMetal = (metal) => {
      selects.forEach((select) => {
        const options = Array.from(select.options);
        const current = select.selectedOptions && select.selectedOptions[0];
        const rest = current ? current.dataset.rest : '';
        const match =
          options.find((option) => option.dataset.option1 === metal && option.dataset.rest === rest) ||
          options.find((option) => option.dataset.option1 === metal && option.dataset.rest.indexOf('6 mm') !== -1) ||
          options.find((option) => option.dataset.option1 === metal);
        if (match) select.value = match.value;
      });
    };

    bundle.addEventListener('change', (event) => {
      if (event.target.matches('[data-eth-metal]')) applyMetal(event.target.value);
      update();
    });

    if (addButton) {
      addButton.addEventListener('click', () => {
        const items = selects
          .filter((select) => select.value)
          .map((select) => ({ id: Number(select.value), quantity: 1 }));
        if (!items.length) return;
        addButton.setAttribute('aria-busy', 'true');
        if (error) error.setAttribute('hidden', '');
        postJSON(cartAddUrl(), { items })
          .then(({ ok, data }) => {
            if (!ok || data.status) {
              addButton.removeAttribute('aria-busy');
              showError(error, data.description || data.message);
              return;
            }
            window.location.href = cartUrl();
          })
          .catch(() => {
            addButton.removeAttribute('aria-busy');
            showError(error);
          });
      });
    }

    update();
  };

  const init = () => {
    revealNotes();
    followVariant();
    document.querySelectorAll('[data-eth-bundle]').forEach(initBundle);
  };

  /* Theme editor re-renders a section on every change */
  document.addEventListener('shopify:section:load', (event) => {
    revealNotes(event.target);
    event.target.querySelectorAll('[data-eth-bundle]').forEach(initBundle);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
